import React, { useMemo } from "react";
import { Box } from "@mui/material";
import { MONTHS } from "../catalog-planner/utils";
import { gridLines } from "./forecastTree";

export const fmtMoney = (v) => {
  if (v == null || Number.isNaN(Number(v))) return "–";
  const abs = Math.abs(v);
  const sign = v < 0 ? "-" : "";
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e4) return `${sign}$${Math.round(abs / 1e3)}K`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(1)}K`;
  return `${sign}$${Math.round(abs)}`;
};
export const fmtUnits = (v) =>
  v == null || Number.isNaN(Number(v)) ? "–" : Math.round(v).toLocaleString();

/**
 * Read-only plan vs forecast grid for one node (category, product or SKU):
 * Plan / Actual / Forecast / Δ vs Plan / cumulative in $, then the same in
 * units. Closed months show actuals; open months the forecast.
 *
 * Plain <table> styled once on the wrapper (no per-cell MUI styling) and
 * memoised: a page can show hundreds of these when products and SKUs are
 * expanded, so each must be cheap and must not re-render for unrelated
 * state changes.
 */
// Built once per theme, so every grid shares one generated style.
const gridSx = (dark) => {
  const c = dark
    ? { border: "#333", sub: "#1e1e1e", muted: "#666", sec: "#aaa", shade: "#1e1e1e" }
    : { border: "#e0e0e0", sub: "#fafafa", muted: "#bbb", sec: "#666", shade: "#f9f9f9" };
  return {
    overflowX: "auto",
    "& table": { minWidth: 980, width: "100%", borderCollapse: "collapse", fontSize: 11.5 },
    "& td, & th": {
      py: 0.6,
      px: 0.75,
      borderBottom: `1px solid ${c.border}`,
      whiteSpace: "nowrap",
      textAlign: "center",
      fontWeight: 400,
    },
    "& tbody th": {
      textAlign: "left",
      fontSize: 9.5,
      fontWeight: 700,
      letterSpacing: 0.4,
      color: c.sec,
      bgcolor: c.sub,
      borderRight: `1px solid ${c.border}`,
      textTransform: "uppercase",
      position: "sticky",
      left: 0,
      width: 120,
    },
    "& thead th": { fontSize: 10, fontWeight: 700, color: c.sec, bgcolor: c.sub },
    "& thead th.open": { color: "#1976d2" },
    "& thead th.ytd": { bgcolor: "#1976d2", color: "#fff" },
    "& thead th.yr": { bgcolor: "#1565c0", color: "#fff" },
    "& td.ytd": { color: "#1976d2", fontWeight: 500 },
    "& td.yr": { color: "#1565c0", fontWeight: 500 },
    "& tr.d td": { bgcolor: c.shade },
    "& .m": { color: c.muted },
    "& .f": { fontStyle: "italic" },
    "& .up": { color: "#2e7d32", fontWeight: 600 },
    // Below plan: amber, not red — red is kept for problems.
    "& .down": { color: dark ? "#f0a55a" : "#b45309", fontWeight: 600 },
  };
};
const GRID_SX = { light: gridSx(false), dark: gridSx(true) };

function ForecastGrid({ node, closed, darkMode }) {
  const money = useMemo(() => gridLines(node, closed, ""), [node, closed]);
  const units = useMemo(() => gridLines(node, closed, "_units"), [node, closed]);

  const val = (v, fmt, i) =>
    v == null ? (
      <span className="m">–</span>
    ) : (
      <span className={i != null && i >= closed ? "f" : undefined}>{fmt(v)}</span>
    );
  const delta = (v, fmt) =>
    v == null ? (
      <span className="m">–</span>
    ) : (
      <span className={v > 0 ? "up" : v < 0 ? "down" : undefined}>
        {v > 0 ? "+" : ""}
        {fmt(v)}
      </span>
    );

  const line = (label, values, fmt, ytd, year, { isDelta = false } = {}) => (
    <tr className={isDelta ? "d" : undefined}>
      <th>{label}</th>
      {values.map((v, i) => (
        <td key={i}>{isDelta ? delta(v, fmt) : val(v, fmt, i)}</td>
      ))}
      <td className="ytd">{isDelta ? delta(ytd, fmt) : val(ytd, fmt)}</td>
      <td className="yr">{isDelta ? delta(year, fmt) : val(year, fmt)}</td>
    </tr>
  );

  return (
    <Box sx={darkMode ? GRID_SX.dark : GRID_SX.light}>
      <table>
        <thead>
          <tr>
            <th />
            {MONTHS.map((m, i) => (
              <th key={m} className={i < closed ? undefined : "open"}>
                {m.toUpperCase()}
              </th>
            ))}
            <th className="ytd">YTD</th>
            <th className="yr">YEAR</th>
          </tr>
        </thead>
        <tbody>
          {line("Plan ($)", money.plan, fmtMoney, money.ytd.plan, money.year.plan)}
          {line("Actual ($)", money.actual, fmtMoney, money.ytd.actual, null)}
          {line("Forecast ($)", money.forecast, fmtMoney, null, money.year.forecast)}
          {line("Δ vs plan ($)", money.delta, fmtMoney, money.ytd.delta, money.year.delta, { isDelta: true })}
          {line("Cumul. plan", money.cum_plan, fmtMoney, null, money.year.plan)}
          {line("Cumul. outlook", money.cum_outlook, fmtMoney, null, money.year.outlook)}
          {line("Plan units", units.plan, fmtUnits, units.ytd.plan, units.year.plan)}
          {line("Actual units", units.actual, fmtUnits, units.ytd.actual, null)}
          {line("Forecast units", units.forecast, fmtUnits, null, units.year.forecast)}
          {line("Δ units", units.delta, fmtUnits, units.ytd.delta, units.year.delta, { isDelta: true })}
        </tbody>
      </table>
    </Box>
  );
}

export default React.memo(ForecastGrid);
